import React, { useEffect, useRef, useState } from 'react';
import { NotesOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { PatientV2TriageItemResponse, PatientV2TriageNotePayload } from '../../../../services/patients/patientsV2Service';

const stages = ['Geral', 'Dados do paciente', 'Informações clínicas', 'Documentos', 'Regulatória', 'Triagem'];

const statusTone = (status?: string) => {
  if (['Ativo', 'Aprovado', 'Deferido provisório'].includes(status ?? '')) return { color: '#287a20', background: '#edf7f0' };
  if (['Pendente', 'Pendenciado', 'Indeferimento provisório'].includes(status ?? '')) return { color: '#a86200', background: '#fff1dc' };
  if (['Indeferido'].includes(status ?? '')) return { color: '#b42318', background: '#fcebe8' };
  if (['Não aprovado', 'Reprovado'].includes(status ?? '')) return { color: '#b42318', background: '#fcebe8' };
  return { color: '#1769aa', background: '#eaf4fb' };
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR');
};

const stageAddsContext = (eventType: string, stage: string) => {
  const normalizedEvent = eventType
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  const normalizedStage = stage
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return Boolean(normalizedStage) && normalizedStage !== 'geral' && !normalizedEvent.includes(normalizedStage);
};

const ExpandableMessage = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    setCanExpand(content.scrollHeight > content.clientHeight + 1);
  }, [text]);

  return (
    <Box sx={{ mt: 0.35 }}>
      <Typography
        ref={contentRef}
        variant="body2"
        sx={{
          whiteSpace: 'pre-wrap',
          color: '#435047',
          fontSize: { xs: '0.75rem', sm: '0.78rem' },
          lineHeight: 1.4,
          ...(expanded
            ? {}
            : {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 4,
                overflow: 'hidden',
              }),
        }}
      >
        {text}
      </Typography>
      {canExpand && (
        <Button
          size="small"
          onClick={() => setExpanded((value) => !value)}
          sx={{ mt: 0.15, minWidth: 0, p: 0, color: '#276b35', fontSize: '0.7rem', fontWeight: 700, textTransform: 'none' }}
          aria-expanded={expanded}
        >
          {expanded ? 'Ver menos' : 'Ver mais'}
        </Button>
      )}
    </Box>
  );
};

interface PatientTriageTimelineProps {
  items: PatientV2TriageItemResponse[];
  loading?: boolean;
  error?: string;
  canAddNote?: boolean;
  onCreateNote?: (payload: PatientV2TriageNotePayload) => Promise<void>;
}

export const PatientTriageTimeline = ({ items, loading = false, error = '', canAddNote = false, onCreateNote }: PatientTriageTimelineProps) => {
  const [stage, setStage] = useState('Geral');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [noteError, setNoteError] = useState('');
  const messageContainer = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = messageContainer.current;
    if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [items, loading]);

  const saveNote = async () => {
    if (!message.trim()) {
      setNoteError('Informe a mensagem da anotação.');
      return;
    }
    if (!onCreateNote) return;

    setSaving(true);
    setNoteError('');
    try {
      await onCreateNote({ stage, message: message.trim() });
      setMessage('');
      setStage('Geral');
    } catch (requestError: any) {
      setNoteError(requestError?.message ?? 'Não foi possível registrar a anotação.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        p: { xs: 0.75, sm: 1 },
        border: '1px solid #d2d9d4',
        borderRadius: 1.25,
        backgroundColor: '#f1f3f2',
      }}
    >
      {loading ? (
        <Box sx={{ minHeight: 130, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Box ref={messageContainer} sx={{ width: '100%', maxHeight: 270, minHeight: items.length > 0 ? 82 : 64, overflowY: 'auto', pr: 0.5 }}>
          {items.length === 0 ? (
            <Box sx={{ minHeight: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Nenhuma etapa de triagem registrada.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0}>
              {items.map((item, index) => {
                const status = statusTone(item.regulatoryStatus);
                const showStage = stageAddsContext(item.eventType, item.stage || 'Geral');
                const isNote = item.entryKind === 'note';
                return (
                  <Box
                    key={item.id}
                    sx={{
                      px: { xs: 0.5, sm: 0.75 },
                      py: 0.8,
                      wordBreak: 'break-word',
                      borderBottom: index < items.length - 1 ? '1px solid #d8dfda' : 0,
                      borderLeft: isNote ? '2px solid #9b7bbd' : '2px solid #6c9a72',
                      backgroundColor: isNote ? 'rgba(245, 239, 250, 0.55)' : 'transparent',
                    }}
                  >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.35, sm: 1 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
                        {isNote && <NotesOutlined sx={{ color: '#76529c', fontSize: 15 }} />}
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.78rem', sm: '0.82rem' }, fontWeight: 700, color: '#314238' }}>
                          {item.eventType}
                        </Typography>
                        {showStage && (
                          <Typography variant="caption" sx={{ color: '#68756c', fontSize: '0.68rem' }}>
                            ({item.stage})
                          </Typography>
                        )}
                      </Stack>
                      {item.regulatoryStatus && (
                        <Chip
                          label={item.regulatoryStatus}
                          size="small"
                          sx={{ height: 19, fontSize: '0.66rem', color: status.color, backgroundColor: status.background, fontWeight: 700 }}
                        />
                      )}
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#748078', fontSize: '0.66rem' }}>
                      {formatDate(item.createdAt)} · {item.createdByName || 'Sistema'}
                    </Typography>
                    {item.detailsText && <ExpandableMessage text={item.detailsText} />}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      )}

      {canAddNote && (
        <Stack spacing={0.75} sx={{ pt: 0.75, borderTop: '1px solid #d2d9d4' }}>
          {noteError && (
            <Alert severity="error" onClose={() => setNoteError('')}>
              {noteError}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems="stretch">
            <TextField
              select
              label="Etapa"
              value={stage}
              onChange={(event) => setStage(event.target.value)}
              size="small"
              sx={{
                width: { xs: '100%', sm: 190 },
                flexShrink: 0,
                '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#ffffff' },
                '& .MuiInputLabel-root': { fontSize: '0.78rem' },
              }}
            >
              {stages.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Escreva sua mensagem..."
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              size="small"
              fullWidth
              disabled={saving}
              inputProps={{ maxLength: 4000 }}
              sx={{ '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#ffffff' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
            />
            <Button
              variant="contained"
              onClick={() => void saveNote()}
              disabled={saving}
              sx={{ height: 36, minWidth: 88, flexShrink: 0, textTransform: 'none', borderRadius: 1, backgroundColor: '#2f7d32', '&:hover': { backgroundColor: '#246327' } }}
            >
              {saving ? 'Enviando...' : 'Enviar'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};
